// =============================================================
// AIRLINE TRANSITION — CONTENT GENERATOR
// Generates the full bilingual content for a transition landing
// page from a minimal airline config. Keeps content consistent
// across the 27 airlines while letting each one have its
// airline-specific angle, hubs, and tone.
// =============================================================

// ----- Shared link constants -----
const ACADEMY_CTA = { href: '/academy', label: { en: 'Discover the 3-day Academy', fr: 'Découvrir l’Academy 3 jours', de: 'Die 3-Tage-Academy entdecken', es: 'Descubrir la Academy 3 días', it: 'Scopri l’Academy di 3 giorni', pt: 'Descobrir a Academy de 3 dias', nl: 'Ontdek de 3-daagse Academy', pl: 'Poznaj Academy 3-dniową', no: 'Oppdag den 3-dagers Academy', sv: 'Upptäck den 3-dagars Academy', fi: 'Tutustu 3 päivän Academyyn', tr: '3 günlük Academy’yi keşfedin', el: 'Ανακαλύψτε την 3ήμερη Academy', ro: 'Descoperă Academia 3 zile' } };
const CATERING_CTA = { href: '/academy/catering-operations', label: { en: 'Standalone Catering & Operations Training', fr: 'Formation Catering & Operations', de: 'Eigenständiges Catering- & Operations-Training', es: 'Formación independiente de Catering & Operations', it: 'Formazione Catering & Operations', pt: 'Formação Catering & Operations', nl: 'Standalone Catering & Operations Training', pl: 'Samodzielne szkolenie Catering & Operations', no: 'Frittstående Catering & Operations Training', sv: 'Fristående Catering & Operations Training', fi: 'Itsenäinen Catering & Operations -koulutus', tr: 'Bağımsız Catering & Operations Eğitimi', el: 'Αυτοτελής Εκπαίδευση Catering & Operations', ro: 'Curs independent Catering & Operations' } };
const CV_LINK = '/mentorship';

// ----- Shared default hero (same cinematic crew-in-hangar shot) -----
const DEFAULT_HERO =
  'https://customer-assets.emergentagent.com/job_gate33-preview/artifacts/4lcxvycf_ChatGPT%20Image%20May%2028%2C%202026%20at%2001_44_39%20PM.png';

// ----- Why-transition library (reused, varied per airline category) -----
// Each item is { kicker, title, body } and supports per-airline override
const WHY_LIBRARY = {
  en: {
    compensation: {
      kicker: 'Compensation',
      title: 'A different economic reality',
      body:
        'Private flight attendants are routinely paid 2–4× their commercial counterparts, with day rates on freelance contracts that simply do not exist in scheduled airlines.',
    },
    lifestyle: {
      kicker: 'Lifestyle',
      title: 'No more rosters. No more standby.',
      body:
        'Trips are planned. Most flights are one to three sectors. You sleep in five-star hotels, not crew rest. You return home with proper recovery — not back-to-back rotations.',
    },
    craft: {
      kicker: 'Craft',
      title: 'You finally serve guests, not passengers',
      body:
        'Two to four people in a cabin you have prepared yourself. Service becomes hospitality. Discretion becomes a skill. The job becomes the one you actually trained for.',
    },
    geography_default: ({ hubs, country }) => ({
      kicker: 'Geography',
      title: `${hubs[0]} and ${country} as gateways`,
      body: `${country} sits inside one of the most active private aviation networks in Europe. Operators around ${hubs.join(', ')} hire continuously — provided you arrive with the right operational profile.`,
    }),
  },
  fr: {
    compensation: {
      kicker: 'Rémunération',
      title: 'Une réalité économique différente',
      body:
        'Les PNC privé sont régulièrement rémunérés 2 à 4× plus que leurs homologues commerciaux, avec des tarifs journaliers en freelance qui n’existent simplement pas en compagnie régulière.',
    },
    lifestyle: {
      kicker: 'Rythme de vie',
      title: 'Fin des plannings. Fin du standby.',
      body:
        'Les vols sont planifiés. La majorité fait un à trois secteurs. Vous dormez en hôtels 5*, pas en crew rest. Vous rentrez avec un vrai temps de récupération — pas des rotations enchaînées.',
    },
    craft: {
      kicker: 'Métier',
      title: 'Vous servez enfin des invités, pas des passagers',
      body:
        'Deux à quatre personnes dans une cabine que vous avez préparée vous-même. Le service devient hospitalité. La discrétion devient une compétence. Le métier devient celui pour lequel vous étiez réellement faite.',
    },
    geography_default: ({ hubs, country }) => ({
      kicker: 'Géographie',
      title: `${hubs[0]} et ${country} comme portes d’entrée`,
      body: `${country} est au cœur de l’un des réseaux d’aviation privée les plus actifs d’Europe. Les opérateurs autour de ${hubs.join(', ')} recrutent en continu — à condition d’arriver avec le bon profil opérationnel.`,
    }),
  },
};

// ----- Generic body copy that adapts to airline + language -----
const COPY = {
  en: {
    introTitle: 'You are not starting from zero. You are switching disciplines.',
    introParas: (a) => [
      `${a.name} gave you the service polish, the operational discipline, the languages, and the calm under pressure that operators notice immediately. That is the foundation — not the destination.`,
      'What changes in private aviation is the operating model. No senior, no procedure manual, no galley team. You become the entire cabin — sometimes the only crew member on board. Operators do not hire for hours flown. They hire for autonomy, judgement, and the ability to deliver UHNW service alone.',
    ],
    whyTitle: (a) => `Why ${a.name} crew make the switch`,
    gapTitle: (a) => `The gap nobody at ${a.name} will tell you about`,
    gapParas: [
      'Airline training teaches you to follow procedures. Private aviation requires you to create them. There is no SOP for catering a kosher meal at 4am from a small FBO. No checklist for managing a passenger change 90 minutes before departure.',
      'The skills that get you hired — solo operational capability, catering ordering, galley competence across multiple aircraft types, discretion around UHNW passengers — are exactly the skills no commercial carrier ever develops. That is the gap we close.',
    ],
    programTitle: 'What Gate 33 builds with you',
    programText: (a) =>
      `Gate 33 is the operational training ${a.name} never had a reason to give you. We translate your existing service intelligence into the private-aviation operating model — through the program, the consulting, and the tools we built ourselves.`,
    programItems: [
      'The full operational cycle of a private flight — brief, catering, galley, service, recovery',
      'Aircraft-by-aircraft fluency (Global, Falcon, Challenger, Gulfstream, Embraer)',
      'UHNW service codes, discretion protocols, and reading the cabin',
      'Crisis judgement: 27 real-world scenarios decoded the way premium operators expect',
      'Your CV and positioning — rebuilt for operators, not airline HR',
    ],
    academyTitle: 'Join the 3-Day Online Training — €600, from anywhere',
    academyText:
      'The flagship Gate 33 training is now fully online — 3 consecutive live online days with your trainer, covering 14 modules. No recorded videos. It compresses what most cabin crew spend years figuring out on their own: operational autonomy, catering logic, aircraft fluency, and the recruitment positioning operators actually respond to. €600, or 3 × €200.',
    cvTitle: 'Your CV is the first cabin you walk into.',
    cvParas: [
      'Operators do not read airline CVs the way airline HR does. A two-page document listing fleet types and seniority will be filtered out in seconds. What works is a single-page operator-grade profile that signals autonomy, mobility, and service intelligence.',
      'We rebuild it with you, line by line, so that when an operator opens the file they see a private flight attendant — not an airline FA hoping for a chance.',
    ],
    cvCtaLabel: 'Upgrade your profile',
    communityTitle: 'Inside the room before you apply.',
    communityText:
      'The Gate 33 community gathers cabin crew already inside private aviation and those preparing the transition. Operator listings, real cabin debriefs, recruiter conversations — the kind of access airline forums simply do not have.',
    communityWhatsapp: 'Join the WhatsApp community',
    communityLinkedin: 'Follow Gate 33 on LinkedIn',
    finalTagline: (a) => `${a.name} gave you the standard. We give you the next altitude.`,
    finalText:
      'The transition is not a leap. It is a re-engineering of your operational profile. The 3-Day Online Training is the most direct path through it — €600, three live online days with your trainer.',
    finalCtaLabel: 'See the training',
    eyebrow: (a) => `For ${a.name} Cabin Crew`,
    heroTitle: (a) => `From ${a.name} to Private Aviation.`,
    heroSubtitle:
      'You already deliver service at the highest commercial standard. Private aviation is a different discipline — and we exist to take you across that line.',
    altLangLabel: 'Read in English',
  },
  fr: {
    introTitle: 'Vous ne partez pas de zéro. Vous changez de discipline.',
    introParas: (a) => [
      `${a.name} vous a donné le sens du service, la rigueur opérationnelle, les langues et le calme sous pression que les opérateurs remarquent immédiatement. C’est la base — pas la destination.`,
      'Ce qui change en aviation privée, c’est le modèle opérationnel. Plus de chef de cabine, plus de manuel de procédure, plus d’équipe galley. Vous devenez l’intégralité de la cabine — parfois la seule personne à bord. Les opérateurs ne recrutent pas sur les heures de vol. Ils recrutent sur l’autonomie, le jugement et la capacité à délivrer un service UHNW seul·e.',
    ],
    whyTitle: (a) => `Pourquoi les PNC ${a.name} franchissent le pas`,
    gapTitle: (a) => `Le fossé que personne chez ${a.name} ne vous dira`,
    gapParas: [
      'La formation compagnie vous apprend à suivre des procédures. L’aviation privée exige que vous les créiez. Aucune procédure pour un catering casher à 4h du matin depuis un petit FBO. Aucune checklist pour gérer un changement de passager 90 minutes avant le départ.',
      'Les compétences qui font qu’on vous recrute — autonomie opérationnelle, gestion du catering, maîtrise galley sur plusieurs types d’avions, discrétion auprès des passagers UHNW — sont précisément celles qu’aucune compagnie commerciale ne développe jamais. C’est le fossé que nous comblons.',
    ],
    programTitle: 'Ce que Gate 33 construit avec vous',
    programText: (a) =>
      `Gate 33 est la formation opérationnelle que ${a.name} n’a jamais eu de raison de vous donner. Nous traduisons votre intelligence de service existante dans le modèle opérationnel de l’aviation privée — à travers le programme, le consulting et les outils que nous avons construits nous-mêmes.`,
    programItems: [
      'Le cycle opérationnel complet d’un vol privé — briefing, catering, galley, service, récupération',
      'La maîtrise avion par avion (Global, Falcon, Challenger, Gulfstream, Embraer)',
      'Les codes de service UHNW, les protocoles de discrétion, la lecture de la cabine',
      'Le jugement en situation de crise : 27 scénarios réels décodés comme l’attendent les opérateurs premium',
      'Votre CV et votre positionnement — reconstruits pour les opérateurs, pas pour les RH compagnie',
    ],
    academyTitle: 'Rejoignez le 3-Day Online Training — €600, depuis n’importe où',
    academyText:
      'Le programme phare Gate 33 est désormais entièrement en ligne — 3 journées consécutives en direct avec votre formateur, couvrant 14 modules. Pas de vidéos enregistrées. Il compresse ce que la plupart des PNC mettent des années à comprendre seuls : autonomie opérationnelle, logique du catering, maîtrise des avions et positionnement de recrutement auquel les opérateurs répondent vraiment. €600, ou 3 × €200.',
    cvTitle: 'Votre CV est la première cabine dans laquelle vous entrez.',
    cvParas: [
      'Les opérateurs ne lisent pas un CV compagnie comme le ferait un service RH. Un document de deux pages listant flottes et anciennetés est filtré en quelques secondes. Ce qui fonctionne, c’est un profil d’une page, calibré opérateur, qui signale autonomie, mobilité et intelligence de service.',
      'Nous le reconstruisons avec vous, ligne par ligne, pour qu’à l’ouverture du fichier un opérateur voie un·e PNC privé·e — pas un·e PNC compagnie en quête d’une opportunité.',
    ],
    cvCtaLabel: 'Optimiser mon profil',
    communityTitle: 'Dans la pièce, avant même de postuler.',
    communityText:
      'La communauté Gate 33 réunit les PNC déjà en aviation privée et celles et ceux qui préparent la transition. Annonces opérateurs, débriefs cabine réels, conversations avec recruteurs — un accès que les forums compagnie n’offrent simplement pas.',
    communityWhatsapp: 'Rejoindre la communauté WhatsApp',
    communityLinkedin: 'Suivre Gate 33 sur LinkedIn',
    finalTagline: (a) => `${a.name} vous a donné le standard. Nous vous donnons l’altitude suivante.`,
    finalText:
      'La transition n’est pas un saut. C’est une réingénierie de votre profil opérationnel. Le 3-Day Online Training est le chemin le plus direct — €600, 3 journées en direct avec votre formateur.',
    finalCtaLabel: 'Voir la prochaine session',
    eyebrow: (a) => `Pour les PNC ${a.name}`,
    heroTitle: (a) => `De ${a.name} à l’Aviation Privée.`,
    heroSubtitle:
      'Vous délivrez déjà un service au plus haut standard commercial. L’aviation privée est une autre discipline — et nous existons pour vous faire passer cette ligne.',
    altLangLabel: 'Lire en français',
  },
};

// ----- For non-EN/FR languages we keep most of the page in English
// with a localized eyebrow / hero / final CTA. This delivers solid SEO
// on the second-language search query without requiring 14 full translations.
const LIGHT_LOCALES = {
  de: {
    eyebrow: (a) => `Für ${a.name} Kabinenpersonal`,
    heroTitle: (a) => `Von ${a.name} in die Geschäftsluftfahrt.`,
    heroSubtitle:
      'Sie liefern bereits Service auf höchstem kommerziellen Niveau. Die Geschäftsluftfahrt ist eine andere Disziplin — und Gate 33 begleitet Sie über diese Linie.',
    altLangLabel: 'Lesen Sie auf Deutsch',
  },
  es: {
    eyebrow: (a) => `Para la tripulación de cabina de ${a.name}`,
    heroTitle: (a) => `De ${a.name} a la aviación privada.`,
    heroSubtitle:
      'Ya ofrece servicio al más alto nivel comercial. La aviación privada es otra disciplina — y Gate 33 le acompaña al otro lado de esa línea.',
    altLangLabel: 'Leer en español',
  },
  it: {
    eyebrow: (a) => `Per gli equipaggi di cabina di ${a.name}`,
    heroTitle: (a) => `Da ${a.name} all’aviazione privata.`,
    heroSubtitle:
      'Offre già un servizio al più alto standard commerciale. L’aviazione privata è una disciplina diversa — e Gate 33 esiste per accompagnarla al di là di questa linea.',
    altLangLabel: 'Leggi in italiano',
  },
  pt: {
    eyebrow: (a) => `Para tripulação de cabine ${a.name}`,
    heroTitle: (a) => `Da ${a.name} para a aviação privada.`,
    heroSubtitle:
      'Já oferece um serviço ao mais alto padrão comercial. A aviação privada é outra disciplina — e a Gate 33 acompanha-o através dessa linha.',
    altLangLabel: 'Ler em português',
  },
  nl: {
    eyebrow: (a) => `Voor ${a.name} cabinepersoneel`,
    heroTitle: (a) => `Van ${a.name} naar de zakenluchtvaart.`,
    heroSubtitle:
      'U levert al service op het hoogste commerciële niveau. Zakenluchtvaart is een andere discipline — en Gate 33 helpt u over die lijn.',
    altLangLabel: 'Lees in het Nederlands',
  },
  pl: {
    eyebrow: (a) => `Dla personelu pokładowego ${a.name}`,
    heroTitle: (a) => `Z ${a.name} do lotnictwa biznesowego.`,
    heroSubtitle:
      'Już teraz zapewniacie najwyższy poziom obsługi komercyjnej. Lotnictwo biznesowe to inna dyscyplina — Gate 33 prowadzi was przez tę granicę.',
    altLangLabel: 'Czytaj po polsku',
  },
  no: {
    eyebrow: (a) => `For ${a.name} kabinpersonale`,
    heroTitle: (a) => `Fra ${a.name} til privatfly.`,
    heroSubtitle:
      'Du leverer allerede service på høyeste kommersielle nivå. Privatfly er en annen disiplin — og Gate 33 hjelper deg over den linjen.',
    altLangLabel: 'Les på norsk',
  },
  sv: {
    eyebrow: (a) => `För ${a.name} kabinpersonal`,
    heroTitle: (a) => `Från ${a.name} till privatflyg.`,
    heroSubtitle:
      'Du levererar redan service på högsta kommersiella nivå. Privatflyg är en annan disciplin — och Gate 33 finns för att ta dig över den linjen.',
    altLangLabel: 'Läs på svenska',
  },
  fi: {
    eyebrow: (a) => `${a.name} matkustamohenkilökunnalle`,
    heroTitle: (a) => `${a.name}sta yksityisilmailuun.`,
    heroSubtitle:
      'Tarjoat jo palvelua kaupallisen lentoliikenteen korkeimmalla tasolla. Yksityisilmailu on eri kuria — ja Gate 33 vie sinut sen yli.',
    altLangLabel: 'Lue suomeksi',
  },
  tr: {
    eyebrow: (a) => `${a.name} kabin ekibi için`,
    heroTitle: (a) => `${a.name}’dan özel havacılığa.`,
    heroSubtitle:
      'Zaten en yüksek ticari hizmet standardını sunuyorsunuz. Özel havacılık farklı bir disiplindir — ve Gate 33, sizi bu çizginin diğer tarafına geçirmek için var.',
    altLangLabel: 'Türkçe okuyun',
  },
  el: {
    eyebrow: (a) => `Για το πλήρωμα καμπίνας της ${a.name}`,
    heroTitle: (a) => `Από την ${a.name} στην ιδιωτική αεροπορία.`,
    heroSubtitle:
      'Παρέχετε ήδη υπηρεσία στο υψηλότερο εμπορικό επίπεδο. Η ιδιωτική αεροπορία είναι διαφορετική πειθαρχία — και η Gate 33 σας περνά αυτή τη γραμμή.',
    altLangLabel: 'Διαβάστε στα ελληνικά',
  },
  ro: {
    eyebrow: (a) => `Pentru echipajul de cabină ${a.name}`,
    heroTitle: (a) => `De la ${a.name} la aviația privată.`,
    heroSubtitle:
      'Oferiți deja un serviciu la cel mai înalt standard comercial. Aviația privată este o disciplină diferită — iar Gate 33 vă conduce dincolo de această linie.',
    altLangLabel: 'Citește în română',
  },
};

// ----- Geography variant for low-cost / multi-base airlines -----
const LOWCOST_GEO = {
  en: ({ hubs }) => ({
    kicker: 'Geography',
    title: 'Bases that already think internationally',
    body: `Operating from ${hubs.join(', ')} means you are already used to mobility, language switches, and short turnarounds — three habits operators value the moment you mention them in the room.`,
  }),
  fr: ({ hubs }) => ({
    kicker: 'Géographie',
    title: 'Des bases qui pensent déjà à l’international',
    body: `Opérer depuis ${hubs.join(', ')} signifie que vous êtes déjà habitué·e à la mobilité, aux changements de langue et aux rotations courtes — trois habitudes que les opérateurs valorisent dès que vous les mentionnez.`,
  }),
};

// =============================================================
// MAIN GENERATOR
// Generates ONE language: English. Air France keeps its
// hand-crafted EN + FR pages. The 26 catalog airlines are
// English-only by design — a clean, consistent premium feel.
// (Full translations can be added later for priority airlines.)
// =============================================================
export const generateAirlineContent = (airline, lang) => {
  if (lang !== 'en') return null; // generator only handles EN
  const copy = COPY.en;
  const why = WHY_LIBRARY.en;

  const primaryPath = `/transition/${airline.slug}`;

  // Which "why" items to pick
  const geoItem =
    airline.category === 'lowcost'
      ? LOWCOST_GEO.en({ hubs: airline.hubs })
      : why.geography_default({ hubs: airline.hubs, country: airline.country });

  const whyItems = [why.compensation, why.lifestyle, why.craft, geoItem];

  const ov = (airline.overrides && airline.overrides.en) || {};
  const eyebrow = ov.eyebrow ?? copy.eyebrow(airline);
  const heroTitle = ov.heroTitle ?? copy.heroTitle(airline);
  const heroSubtitle = ov.heroSubtitle ?? copy.heroSubtitle;

  return {
    lang: 'en',
    path: primaryPath,
    altLangPath: null, // no secondary language for generated airlines
    altLangLabel: null,

    metaTitle: `From ${airline.name} to Private Aviation | Gate 33`,
    metaDescription: `${airline.name} cabin crew transitioning to private aviation: what changes, what operators expect, and how Gate 33 prepares you for the real operational layer.`,

    eyebrow,
    title: heroTitle,
    subtitle: heroSubtitle,

    introTitle: copy.introTitle,
    introText: copy.introParas(airline),

    whyTitle: copy.whyTitle(airline),
    whyItems,

    gapTitle: copy.gapTitle(airline),
    gapText: copy.gapParas,

    programTitle: copy.programTitle,
    programText: copy.programText(airline),
    programItems: copy.programItems,

    academyTitle: copy.academyTitle,
    academyText: copy.academyText,
    academyCtas: [
      {
        label: ACADEMY_CTA.label.en,
        href: ACADEMY_CTA.href,
        testId: `airline-academy-cta-main-${airline.slug}`,
        variant: 'primary',
      },
      {
        label: CATERING_CTA.label.en,
        href: CATERING_CTA.href,
        testId: `airline-academy-cta-catering-${airline.slug}`,
        variant: 'ghost',
      },
    ],

    cvTitle: copy.cvTitle,
    cvText: copy.cvParas,
    cvCta: {
      label: copy.cvCtaLabel,
      href: CV_LINK,
      testId: `airline-cv-cta-${airline.slug}`,
    },

    communityTitle: copy.communityTitle,
    communityText: copy.communityText,
    communityCtas: [
      {
        label: copy.communityWhatsapp,
        href: 'https://chat.whatsapp.com/Cy777tVbX5h4L4YOBn5FoI?mode=gi_t',
        kind: 'whatsapp',
        testId: `airline-cta-whatsapp-${airline.slug}`,
      },
      {
        label: copy.communityLinkedin,
        href: 'https://www.linkedin.com/company/gate-33/',
        kind: 'linkedin',
        testId: `airline-cta-linkedin-${airline.slug}`,
      },
    ],

    finalCtaTitle: copy.finalTagline(airline),
    finalCtaText: copy.finalText,
    finalCtaLabel: copy.finalCtaLabel,
    finalCtaHref: '/academy',
    finalCtaTestId: `airline-final-cta-${airline.slug}`,
  };
};

export const DEFAULT_AIRLINE_HERO = DEFAULT_HERO;
